import type { ISearchResponse } from 'apiConnector/blotters';
import { IBlotterQuoteItem } from 'models/IBlotterItem';
import { map, type Observable } from 'rxjs';
import { WorkspaceType } from 'state/workspaces/model';
import type { HttpCall, IHttpClient } from 'utils/http/httpClient.model';

import type { Day, Frequency, QuoteInfo, Reader, ScheduleRunStatus, ScheduledRunOccurrence } from './model';

interface Trigger {
  time: string;
  timeZone: string;
}
export interface RangeTrigger extends Trigger {
  startDate: string;
  endDate: string;
  frequency: Frequency;
}
export interface DaysTrigger extends Trigger {
  days: readonly Day[];
}
export interface CreateRunRequest {
  name: string;
  comment?: string;
  rangeTrigger?: RangeTrigger;
  daysTrigger?: DaysTrigger;
  quotes: readonly QuoteInfo[];
  readers: readonly Reader[];
}
export type UpdateRunRequest = DeepPartial<CreateRunRequest> & { status?: ScheduleRunStatus; comment?: string };

export interface Run {
  id: string;
  comment?: string;
  name: string;
  etag: string;
  requester: string;
  status: ScheduleRunStatus;
  rangeTrigger?: RangeTrigger;
  daysTrigger?: DaysTrigger;

  quotes: QuoteInfo[];
  readers: Reader[];

  lastRunTime?: string;
  nextRunTime?: string;
}
interface RunsResponse {
  runs: Run[];
}

export interface EditRunResponse {
  id?: string;
  status?: ScheduleRunStatus;

  etag?: string;

  result?: string;
}
export interface DeleteRunResponse {
  status?: ScheduleRunStatus;
  result?: string;
}
export interface ValidateQuotesRequest {
  quotes?: QuoteInfo[];
}
export interface QuoteValidation extends QuoteInfo {
  isValid: boolean;
}
export interface ValidateQuotesResult {
  results?: QuoteValidation[];
}
export interface IScheduledRunApi {
  createRunRequest(req: CreateRunRequest, workspace?: WorkspaceType): Observable<EditRunResponse>;
  updateRunRequest(
    id: string,
    etag: string,
    req: UpdateRunRequest,
    workspace?: WorkspaceType,
  ): Observable<EditRunResponse>;
  checkNameExists(name: string): Observable<HttpCall<void>>;
  getRuns(): Observable<Run[]>;
  getRunById(id: string): Observable<Run>;
  getOccurrences(runId: string): Observable<readonly ScheduledRunOccurrence[]>;
  getOccurrence(runId: string, occurrenceId: string): Observable<ScheduledRunOccurrence>;
  deleteRunById(id: string): Observable<DeleteRunResponse>;
  getQuotes(fullTextSearch: string): Observable<ISearchResponse<IBlotterQuoteItem>>;

  validateQuotes(request: ValidateQuotesRequest, workspace?: WorkspaceType): Observable<ValidateQuotesResult>;
}

export function scheduledRunsApi(http: IHttpClient): IScheduledRunApi {
  return {
    createRunRequest(req: CreateRunRequest, workspaceType?: WorkspaceType): Observable<EditRunResponse> {
      return http
        .postJSON<EditRunResponse>({
          url: `api/v1/scheduled-runs-request`,
          body: req,
          params: { screenMode: workspaceType },
          tryParseErrorPayload: true,
        })
        .pipe(map(({ payload }) => payload));
    },
    updateRunRequest(
      id: string,
      etag: string,
      req: UpdateRunRequest,
      workspaceType?: WorkspaceType,
    ): Observable<EditRunResponse> {
      return http
        .patch<EditRunResponse>({
          url: `api/v1/scheduled-runs/${id}`,
          body: req,
          tryParseErrorPayload: true,
          params: { screenMode: workspaceType },
          headers: { ETag: etag },
        })
        .pipe(map(({ payload }) => payload));
    },
    checkNameExists(name) {
      return http.head({
        url: `api/v1/scheduled-runs/${name}`,
      });
    },
    getQuotes(fullTextSearch) {
      return http
        .getJSON<ISearchResponse<IBlotterQuoteItem>>({
          url: 'api/v1/scheduled-runs/quotes',
          params: { fullTextSearch },
        })
        .pipe(map(({ payload }) => payload));
    },
    getRuns() {
      return http
        .getJSON<RunsResponse>({
          url: `api/v1/scheduled-runs`,
          tryParseErrorPayload: true,
        })
        .pipe(map(({ payload: { runs } }) => runs));
    },
    getRunById(id: string) {
      return http
        .getJSON<Run>({
          url: `api/v1/scheduled-runs/${id}`,
          tryParseErrorPayload: true,
        })
        .pipe(map(({ payload }) => payload));
    },
    getOccurrences(runId: string) {
      return http
        .getJSON<{ entries: readonly ScheduledRunOccurrence[] }>({
          url: `api/v1/scheduled-runs/${encodeURIComponent(runId)}/occurrences`,
          tryParseErrorPayload: true,
        })
        .pipe(map(({ payload }) => payload.entries?.map(normalizeOccurrence) ?? []));
    },
    getOccurrence(runId: string, occurrenceId: string) {
      return http
        .getJSON<ScheduledRunOccurrence>({
          url: `api/v1/scheduled-runs/${encodeURIComponent(runId)}/occurrences/${encodeURIComponent(occurrenceId)}`,
          tryParseErrorPayload: true,
        })
        .pipe(map(({ payload }) => normalizeOccurrence(payload)));
    },
    deleteRunById(id: string) {
      return http
        .deleteJSON<DeleteRunResponse>({
          url: `api/v1/scheduled-runs/${id}`,
          tryParseErrorPayload: true,
        })
        .pipe(map(({ payload }) => payload));
    },
    validateQuotes(req, workspace) {
      return http
        .postJSON<ValidateQuotesResult>({
          url: 'api/v1/scheduled-runs/quotes/validation',
          params: { screenMode: workspace },
          body: req,
        })
        .pipe(map(({ payload }) => payload));
    },
  };
}

function normalizeOccurrence(occurrence: ScheduledRunOccurrence): ScheduledRunOccurrence {
  return { ...occurrence, quotes: occurrence.quotes ?? [] };
}
